<template>
  <div class="usage-funnel">
    <div class="funnel-header">
      <h3>Admin Usage</h3>
      <span v-if="summary" class="funnel-range">
        {{ summary.totals.events }} events &middot; {{ summary.totals.distinctUsers }} users
      </span>
    </div>

    <div v-if="isLoading" class="funnel-state">Loading usage&hellip;</div>

    <div v-else-if="errorMessage" class="funnel-state funnel-error">
      {{ errorMessage }}
    </div>

    <div v-else-if="!summary" class="funnel-state">
      Choose a date range to see usage.
    </div>

    <div v-else-if="!summary.totals.events" class="funnel-state">
      No activity recorded in this date range.
    </div>

    <div v-else>
      <p v-if="summary.totals.truncated" class="funnel-warning">
        Showing the first {{ summary.totals.events }} events only. Every number below is an undercount.
      </p>

      <div class="tab-row">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="tab-btn"
          :class="{ 'is-active': role === tab.key }"
          @click="role = tab.key"
        >{{ tab.label }}</button>
      </div>

      <template v-for="section in visibleSections">
        <h4 :key="`${section.key}-title`" class="section-title">{{ section.title }}</h4>
        <div :key="section.key" class="stat-grid">
          <div v-for="card in section.cards" :key="card.label" class="stat-cell">
            <div class="stat-label" :class="{ 'has-tip': card.hint }">
              <span v-if="card.hint" class="stat-tip">{{ card.hint }}</span>
              {{ card.label }}
            </div>
            <div class="stat-value">{{ card.value }}</div>
          </div>
        </div>
      </template>

      <template v-if="showGlobal">
        <h4 class="section-title">Activity by event</h4>
        <div class="activity-grid">
          <div v-for="row in activity" :key="row.type" class="activity-row">
            <span class="activity-type">{{ row.type }}</span>
            <span class="activity-count">{{ row.count }}</span>
          </div>
        </div>
      </template>

    </div>
  </div>
</template>

<script>
import Usage from "../../Firestore/Usage.js";

const STAGES = [
  { key: "joined", label: "Joined a project" },
  { key: "assigned", label: "Assigned to a slot" },
  { key: "started", label: "Started a playtest" },
  { key: "completed", label: "Completed the session" },
  { key: "submitted", label: "Submitted the survey" },
];

export default {
  name: "UsageFunnel",
  props: {
    startDate: { type: String, default: "" },
    endDate: { type: String, default: "" },
  },
  data() {
    return {
      summary: null,
      isLoading: false,
      errorMessage: "",
      role: "all",
      tabs: [
        { key: "all", label: "All" },
        { key: "dev", label: "Developers" },
        { key: "tester", label: "Playtesters" },
      ],
    };
  },
  computed: {
    metrics() {
      return this.summary ? this.summary.metrics : null;
    },
    userCards() {
      if (!this.metrics) return [];
      const m = this.metrics;
      const r = this.role;
      return [
        { label: "Active today", value: m.activeUsers.today[r] },
        { label: "Active this week", value: m.activeUsers.weekly[r] },
        { label: "Active this month", value: m.activeUsers.monthly[r] },
        { label: "New today", value: m.newUsers.today[r] },
        { label: "New this week", value: m.newUsers.weekly[r] },
        { label: "New this month", value: m.newUsers.monthly[r] },
      ];
    },
    platformCards() {
      if (!this.metrics || !this.summary.projects) return [];
      const t = this.metrics.timeToFeedback;
      const s = this.summary.surveys;
      const cards = [
        { label: "Projects", value: this.summary.projects.total },
        { label: "New projects", value: this.summary.projects.createdInRange },
        { label: "Days to feedback", value: t.averageDays === null ? "—" : t.averageDays },
        { label: "Awaiting feedback", value: t.awaiting },
      ];

      if (s) {
        cards.push({ label: "Surveys tracked", value: s.tracked });
        cards.push({ label: "Survey responses", value: s.responses });
        cards.push({ label: "Survey completion", value: this.asPercent(s.completionRate) });
      }

      return cards;
    },
    rateCards() {
      if (!this.metrics) return [];
      const m = this.metrics;
      const r = this.role;
      const growth = m.growth[r];
      return [
        { label: "Engagement (PES)", value: this.asPercent(m.pes[r]), hint: "Adoption, stickiness and growth combined" },
        { label: "Adoption", value: this.asPercent(m.adoption[r]), hint: "How much of the product people use" },
        { label: "Stickiness (DAU/MAU)", value: this.asPercent(this.times100(m.stickiness.dauOverMau[r])), hint: "Share of monthly users active daily" },
        { label: "Stickiness (DAU/WAU)", value: this.asPercent(this.times100(m.stickiness.dauOverWau[r])), hint: "Share of weekly users active daily" },
        { label: "Stickiness (WAU/MAU)", value: this.asPercent(this.times100(m.stickiness.wauOverMau[r])), hint: "Share of monthly users active weekly" },
        { label: "Retention", value: this.asPercent(m.retention[r]), hint: "Last month's users who came back" },
        { label: "Growth", value: growth.quickRatio === null ? "—" : growth.quickRatio, hint: "Users gained versus users lost" },
      ];
    },
    funnelCards() {
      if (!this.summary) return [];
      const s = this.summary.funnel.stages;

      return STAGES.map(stage => ({
        label: stage.label,
        value: s[stage.key] ? s[stage.key].users : 0,
        hint: stage.hint,
      }));
    },
    showGlobal() {
      return this.role === "all";
    },
    visibleSections() {
      if (!this.metrics) return [];

      const sections = [
        { key: "users", title: "Users", cards: this.userCards },
        { key: "rates", title: "Engagement", cards: this.rateCards },
      ];

      if (this.showGlobal) {
        sections.push({ key: "platform", title: "Platform", cards: this.platformCards });
        sections.push({ key: "funnel", title: "Playtester funnel", cards: this.funnelCards });
      }

      return sections;
    },
    activity() {
      if (!this.summary) return [];
      const byType = this.summary.byType || {};
      return Object.keys(byType)
        .map(type => ({ type, count: byType[type] }))
        .sort((a, b) => b.count - a.count);
    },
  },
  watch: {
    startDate() { this.load(); },
    endDate() { this.load(); },
  },
  mounted() {
    this.load();
  },
  methods: {
    times100(value) {
      return value === null || value === undefined ? null : Math.round(value * 1000) / 10;
    },
    asPercent(value) {
      return value === null || value === undefined ? "—" : `${value}%`;
    },
    async load() {
      if (!this.startDate || !this.endDate) return;

      this.isLoading = true;
      this.errorMessage = "";

      const res = await Usage.getUsageSummary(this.startDate, this.endDate);

      this.isLoading = false;
      if (res.success) {
        this.summary = res.data;
      } else {
        this.summary = null;
        this.errorMessage = res.error;
      }
    },
  },
};
</script>

<style scoped>
.usage-funnel {
  background-color: white;
  margin-top: 2%;
  min-width: 650px;
  border-radius: 30px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  padding: 24px 3% 28px;
}

.funnel-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
}

.funnel-header h3 {
  margin: 0;
  font-family: "Lato", sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #ff7222;
}

.funnel-range {
  font-size: 13px;
  color: #7a7a7a;
}

.funnel-state {
  padding: 24px 0;
  color: #7a7a7a;
  font-size: 14px;
}

.funnel-error {
  color: #c0392b;
}

.funnel-warning {
  margin: 0 0 16px;
  padding: 10px 14px;
  background: #fff6e5;
  border-left: 3px solid #ffa15c;
  border-radius: 4px;
  font-size: 13px;
  color: #7a5c2e;
}

.tab-row {
  display: flex;
  gap: 8px;
  margin-bottom: 18px;
}

.tab-btn {
  border: 1px solid #e3e3e3;
  background: #fff;
  color: #555;
  border-radius: 20px;
  padding: 6px 16px;
  font-size: 13px;
  cursor: pointer;
}

.tab-btn.is-active {
  background: #ff7222;
  border-color: #ff7222;
  color: #fff;
  font-weight: 700;
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}

.stat-cell {
  background: #fbfbfb;
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 14px 16px 12px;
  text-align: center;
}

.stat-label {
  position: relative;
  font-size: 13px;
  color: #555;
  min-height: 32px;
}

.stat-label.has-tip {
  cursor: help;
}

.stat-label.has-tip:hover {
  color: #ff7222;
}

.stat-tip {
  position: absolute;
  bottom: calc(100% + 9px);
  left: 50%;
  transform: translateX(-50%);
  width: 230px;
  max-width: 70vw;
  padding: 9px 11px;
  background: #333;
  color: #fff;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.45;
  text-align: left;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s ease;
  pointer-events: none;
  z-index: 30;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
}

.stat-tip::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 5px solid transparent;
  border-top-color: #333;
}

.stat-label.has-tip:hover .stat-tip {
  opacity: 1;
  visibility: visible;
}

.stat-value {
  font-family: "Lato", sans-serif;
  font-size: 30px;
  font-weight: 700;
  color: #e94e77;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
}

.section-title {
  margin: 26px 0 12px;
  font-family: "Lato", sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: #444;
}

.activity-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 4px 24px;
}

.activity-row {
  display: flex;
  justify-content: space-between;
  padding: 5px 0;
  border-bottom: 1px solid #f2f2f2;
  font-size: 13px;
}

.activity-type {
  color: #555;
}

.activity-count {
  color: #333;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
</style>



// WEBPACK FOOTER //
// src/components/admin-only/UsageFunnel.vue